import { Button, Label, Switch } from '@playcastdotio/Shadcn';
import { Content } from './Content';
import { useState, useEffect } from 'react';
import constants from '@playcastdotio/GlobalConfig';

export const Privacy = ({
  allowPeerConnections,
  setAllowPeerConnections,
}: {
  allowPeerConnections: boolean;
  setAllowPeerConnections: (allowPeerConnections: boolean) => void;
}) => {
  const [showConfirmDialog, setConfirmDialog] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [internalAllowPeerConnections, setInternalAllowPeerConnections] = useState(allowPeerConnections);

  useEffect(() => {
    if (showConfirmDialog) {
      setIsAnimating(true);
    }
  }, [showConfirmDialog]);

  useEffect(() => {
    setInternalAllowPeerConnections(allowPeerConnections);
  }, [allowPeerConnections]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setConfirmDialog(false);
    }, 200); // Match this with the animation duration
  };

  const handleRequestDataClicked = () => {
    handleClose();
    window.open('https://mail.google.com/mail/?view=cm&fs=1&to=trey@playcast.io&su=Data%20Request', '_blank');
  };

  const handleAllowPeerConnections = (checked: boolean) => {
    setAllowPeerConnections(!checked);
    setInternalAllowPeerConnections(!checked);
  };

  return (
    <Content title='Privacy & Security'>
      <div className='flex h-full flex-1 flex-col space-y-4'>
        <div className='flex flex-1'>
          <div className='mt-auto flex flex-col gap-16'>
            <div className='flex flex-col'>
              <div className='flex gap-2'>
                <Switch
                  id='allow-peer-connections'
                  checked={!internalAllowPeerConnections}
                  onCheckedChange={handleAllowPeerConnections}
                  className='data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
                />
                <div>
                  <Label htmlFor='allow-peer-connections' className='text-md cursor-pointer'>
                    Do not allow peer connections
                  </Label>
                  <p className='text-sm text-gray-400'>Enabling may increase latency</p>
                </div>
              </div>
            </div>
            <div className='flex flex-col'>
              <Label className='font-[Syne] text-gray-400'>View our policies</Label>
              <div className='flex flex-col'>
                <a
                  href='https://playcast.io/privacy-policy/'
                  className='text-md text-amber-400 underline'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Privacy Policy
                </a>
                <a
                  href='https://playcast.io/terms-of-service/'
                  className='text-md text-amber-400 underline'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Terms of Service
                </a>
              </div>
            </div>
            <div className='flex flex-col'>
              <Label className='font-[Syne] text-gray-400'>Request all of my data</Label>
              <Button className='m-0 mt-2 h-fit w-fit bg-amber-400 hover:bg-amber-400/60' onClick={() => setConfirmDialog(true)}>
                Request My Data
              </Button>

              {/* Modal */}
              {showConfirmDialog && (
                <>
                  {/* Backdrop */}
                  <div className={`fixed inset-0 z-[99999]`} onClick={handleClose} />

                  {/* Modal Content */}
                  <div
                    className={`bg-background fixed left-1/2 top-1/2 z-[99999] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg p-6 shadow-lg transition-all duration-200 ease-in-out ${
                      isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                    }`}
                  >
                    <div className='space-y-4'>
                      <div>
                        <h3 className='text-lg font-semibold'>Are you sure?</h3>
                        <p className='mt-2 text-gray-400'>
                          By requesting your data, you will receive an email with all of your data attached.
                        </p>
                      </div>

                      <div className='mt-4 flex justify-end space-x-2'>
                        <Button className='bg-gray-400 hover:bg-gray-600 focus:bg-gray-400 active:bg-gray-600' onClick={handleClose}>
                          Cancel
                        </Button>
                        <Button variant='default' onClick={handleRequestDataClicked}>
                          Confirm
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Div at the bottom for footer text */}
        <div className='mt-auto flex flex-col'>
          {/* Version number */}
          <Label className='mt-10 text-gray-500'>Version {constants.VERSION}</Label>
          {/* Contact */}
        </div>
      </div>
    </Content>
  );
};
